'use client';

import { usePathname, useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import {
  SettingsNav,
  SettingsShell,
  type SettingsNavGroup,
} from '@kit/ui/settings-shell';

import { recordContext, type RecordType } from '../../lib/record-terminology';
import { getVisibleTabs } from './tab-registry';

/**
 * Unified competition/audition Settings navigation (BAL-386).
 *
 * Replaces both the stacked sub-tab row AND the separate top-nav config tabs:
 * every configuration surface — the in-page form sections (General / Public /
 * Access, addressed as `?section=`) and the sibling config routes (Application
 * form / Categories / Offer types) — lives in this single grouped, searchable
 * sidebar. Rendered by `RecordSettingsShell`, so it persists across all config
 * routes. Active state derives from the pathname + `section` query param.
 */

interface SectionItem {
  /** `?section=` value addressed on the Settings (details) page. */
  section: string;
  labelKey: string;
}

interface RouteItem {
  /** Sub-route segment under the record manage root. */
  segment: string;
  labelKey: string;
}

interface Group {
  id: string;
  labelKey: string;
  /** In-page form sections (live on the details/Settings route). */
  sections?: SectionItem[];
  /** Sibling config routes. */
  routes?: RouteItem[];
  /** Competition-only group. */
  competitionOnly?: boolean;
}

const GROUPS: Group[] = [
  {
    id: 'general',
    labelKey: 'auditions:editor.settingsGroups.general',
    sections: [
      { section: 'basic', labelKey: 'auditions:editor.tabs.basic' },
      {
        section: 'requirements',
        labelKey: 'auditions:editor.tabs.requirements',
      },
      { section: 'dates', labelKey: 'auditions:editor.tabs.dates' },
      { section: 'contract', labelKey: 'auditions:editor.tabs.contract' },
      {
        // Application *settings* (fee, dates, invitation-only) — labeled
        // "Entry settings" for competitions so it never collides with the
        // primary-nav "Entries" list workspace. (BAL-386)
        section: 'applications',
        labelKey: 'auditions:editor.tabs.applicationsSettings',
      },
    ],
  },
  {
    id: 'application',
    labelKey: 'auditions:editor.settingsGroups.application',
    routes: [
      {
        segment: 'application-form',
        labelKey: 'auditions:manage.nav.applicationForm',
      },
      {
        segment: 'categories',
        labelKey: 'auditions:manage.nav.categories',
      },
      // Offer types moved to the Offers tab header (P5, 2026-07-02) — its entry
      // point is a "Manage offer types" button there, not a settings route.
    ],
  },
  {
    id: 'access',
    labelKey: 'auditions:editor.settingsGroups.access',
    competitionOnly: true,
    sections: [
      {
        section: 'competitionNetworking',
        labelKey: 'auditions:editor.tabs.competitionNetworking',
      },
    ],
  },
];

/**
 * Resolve the auditions/competition settings navigation into the generic
 * `SettingsNavGroup[]` shape consumed by the shared `@kit/ui/settings-shell`.
 * Owns all the domain logic (record-type forking, i18n, active-state from
 * pathname + `section` query param) that used to live inline in the sidebar.
 */
function useAuditionSettingsGroups(
  root: string,
  recordType: RecordType,
): {
  groups: SettingsNavGroup[];
  searchPlaceholder: string;
  emptyLabel: string;
} {
  const { t } = useTranslation();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const context = recordContext(recordType);
  const isCompetition = recordType === 'competition';
  // Only surface form sections that exist for this record type (the registry
  // forks competition vs audition); keeps the sidebar honest.
  const visibleSectionIds = new Set<string>(
    getVisibleTabs(recordType).map((tab) => tab.id),
  );
  const detailsPath = `${root}/details`;
  const onDetails = pathname === detailsPath;
  const activeSection = onDetails
    ? (searchParams.get('section') ?? 'basic')
    : null;

  const groups: SettingsNavGroup[] = GROUPS.filter(
    (g) => !g.competitionOnly || isCompetition,
  ).map((group) => {
    const sectionItems = (group.sections ?? [])
      .filter((s) => visibleSectionIds.has(s.section))
      .map((s) => ({
        key: s.section,
        label: t(s.labelKey, { context }),
        href: `${detailsPath}?section=${s.section}`,
        active: onDetails && activeSection === s.section,
      }));
    // The "applications" form section reads its own `applicationsSettings`
    // key ("Entry settings") to avoid colliding with the primary-nav
    // "Entries" workspace; the standalone config routes resolve via their
    // own `manage.nav.*` keys.
    const routeItems = (group.routes ?? [])
      .filter((r) => isCompetition || r.segment === 'application-form')
      .map((r) => ({
        key: r.segment,
        label: t(r.labelKey, { context }),
        href: `${root}/${r.segment}`,
        active: pathname === `${root}/${r.segment}`,
      }));
    return {
      id: group.id,
      label: t(group.labelKey, { context }),
      items: [...sectionItems, ...routeItems],
    };
  });

  return {
    groups,
    searchPlaceholder: t('auditions:editor.searchSettings'),
    emptyLabel: t('auditions:editor.noSettingsMatch'),
  };
}

export function SettingsSidebar({
  root,
  recordType,
}: {
  /** Record manage root, e.g. `/home/acme/competitions/<id>`. */
  root: string;
  recordType: RecordType;
}) {
  const { groups, searchPlaceholder, emptyLabel } = useAuditionSettingsGroups(
    root,
    recordType,
  );

  return (
    <SettingsNav
      groups={groups}
      searchPlaceholder={searchPlaceholder}
      emptyLabel={emptyLabel}
    />
  );
}

/**
 * Two-column settings shell: the persistent {@link SettingsSidebar} on the left,
 * the active config surface on the right. Rendered by every config route
 * (details / application-form / categories / offer-types) so the sidebar stays
 * put as the user moves between them.
 */
export function RecordSettingsShell({
  root,
  recordType,
  children,
}: {
  root: string;
  recordType: RecordType;
  children: React.ReactNode;
}) {
  const { groups, searchPlaceholder, emptyLabel } = useAuditionSettingsGroups(
    root,
    recordType,
  );

  return (
    <SettingsShell
      groups={groups}
      searchPlaceholder={searchPlaceholder}
      emptyLabel={emptyLabel}
    >
      {children}
    </SettingsShell>
  );
}
